/// <amd-module name="Test/TestPlansHubRefresh/TestCaseLinkedItems/Redux/TestCaseLinkedItemsSaga" />
import { effects, SagaIterator } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";
import { ITestHubDataService } from "Test/TestPlansHubRefresh/Service/TestHubDataService";
import {
    AssignedTo,
    ChangedDate,
    Id,
    LinkedPanelLastSelectedTab,
    State,
    Title,
    WorkItemBatchSize,
    WorkItemCategoriesForBugsTab,
    WorkItemCategoriesForRequirementsTab,
    WorkItemTypeReferenceNamesForBugsTab,
    WorkItemTypeReferenceNamesForRequirementsTab,
    WorkItemType
} from "Test/TestPlansHubRefresh/TestCaseLinkedItems/Constants";
import { ILinkedWorkItem } from "Test/TestPlansHubRefresh/TestCaseLinkedItems/Redux/TestCaseLinkedItemsContracts";
import * as Resources from "Test/TestPlansHubRefresh/TestCaseLinkedItems/Resources";
import * as Constants from "Test/TestPlansHubRefresh/TestCaseLinkedItems/Constants";
import { TelemetryConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { TelemetryActions } from "Test/TestPlansHubRefresh/TestHub/Redux//TelemetryActions";
import { TestSuite } from "TestPlanCommon/Contracts/TestPlan";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IVssPageContext } from "VSS/Platform/Context";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { WorkItem, WorkItemBatchGetRequest, WorkItemErrorPolicy, WorkItemExpand } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking";
import { TestCaseLinkedItemsActions, TestCaseLinkedItemsActionsTypes } from "./TestCaseLinkedItemsActions";
import { errorHandledSaga } from "Test/TestPlansHubRefresh/TestHub/Util";
import * as WorkItemTracking from "Wit/Clients/Wit/Contracts/WorkItemTracking";

export function* TestCaseLinkedItemsSaga(): SagaIterator {
    yield effects.takeLatest(TestCaseLinkedItemsActionsTypes.fetchLinkedRequirementsOrBugs, _fetchLinkedRequirementsOrBugs);
    yield effects.takeLatest(TestCaseLinkedItemsActionsTypes.saveSelectedTab, _saveSelectedTab);
    yield effects.takeLatest(TestCaseLinkedItemsActionsTypes.fetchLinkedTestSuites, _fetchLinkedTestSuites);
    yield effects.takeLatest(TestCaseLinkedItemsActionsTypes.getInitialDataForLinkedPanel, _getInitialDataForLinkedPanel);
}

export function* fetchLinkedRequirementsOrBugs(
    action: ActionsOfType<TestCaseLinkedItemsActions, TestCaseLinkedItemsActionsTypes.fetchLinkedRequirementsOrBugs>
): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;
    const witClient: IWorkItemTrackingRestClient = (yield effects.call(
        [pageContext, pageContext.getRestClient],
        "IWorkItemTrackingRestClient"
    )) as any;
    const workItem: WorkItem = (yield effects.call(
        [witClient, witClient.getWorkItem],
        action.payload.testCaseId,
        tfsPageData.project.name,
        undefined,
        undefined,
        WorkItemExpand.Relations
    )) as any;
    let workItemIds: number[] = [];
    const linkedBugs: ILinkedWorkItem[] = [];
    const linkedRequirements: ILinkedWorkItem[] = [];

    if (workItem && workItem.relations) {
        // Fetch the work item types using the api "_apis/wit/workitemtypes". 
        // This gets all the work item types along with their reference names.
        // We will use reference names to identify work item category (requirement or bug) 
        // since comparing work item type names fails for non-eu locales
        const workItemTypesList: WorkItemTracking.WorkItemType[] = (yield effects.call(
            [witClient, witClient.getWorkItemTypes],
            tfsPageData.project.name
        )) as any;
        let workItemTypeRefMap = new Map<string, string>();
        for (let i = 0; i < workItemTypesList.length; i++) {
            workItemTypeRefMap.set(workItemTypesList[i].name, workItemTypesList[i].referenceName);
        }
        // for related workItems, parse work item ID from workitem URL as ID is not returned separately by this API
        for (let index = 0; index < workItem.relations.length; index++) {
            const relatedWorkItemUrl: string = workItem.relations[index].url;
            const workItemId = relatedWorkItemUrl.substring(relatedWorkItemUrl.lastIndexOf("/") + 1);
            if (parseInt(workItemId)) {
                workItemIds.push(parseInt(workItemId));
            }
        }
        // work items batch API has a batch size limit. so we call the API multiple times to get all linked workitems
        for (let i = 0; i < workItemIds.length; i += WorkItemBatchSize) {
            let workItemIdsBatch = workItemIds.slice(i, i + WorkItemBatchSize);
            let workItemsBatchRequest: WorkItemBatchGetRequest = {
                $expand: WorkItemExpand.None,
                ids: workItemIdsBatch,
                errorPolicy: WorkItemErrorPolicy.Omit,
                asOf: new Date(),
                fields: [Id, Title, AssignedTo, State, AssignedTo, ChangedDate, WorkItemType]
            };

            // fetch full workitem details for the linked work items
            const workItemList: WorkItem[] = (yield effects.call(
                [witClient, witClient.getWorkItemsBatch],
                workItemsBatchRequest,
                tfsPageData.project.name
            )) as any;
            for (let i = 0; i < workItemList.length; i++) {
                if (workItemList[i].fields) {
                    const latestUpdateDate = new Date(workItemList[i].fields[ChangedDate]);

                    const assignedToField = workItemList[i].fields[AssignedTo];
                    let assignedToFieldString: string = Resources.UnassignedIdentity;
                    if (assignedToField && assignedToField.displayName) {
                        assignedToFieldString = assignedToField.displayName;
                    } else if (assignedToField && assignedToField.uniqueName) {
                        assignedToFieldString = assignedToField.uniqueName;
                    }

                    const linkedWorkItem: ILinkedWorkItem = {
                        id: workItemList[i].fields[Id],
                        title: workItemList[i].fields[Title],
                        assignedTo: assignedToFieldString,
                        state: workItemList[i].fields[State],
                        latestUpdate: latestUpdateDate ? latestUpdateDate.toLocaleString() : "",
                        workItemType: workItemList[i].fields[WorkItemType]
                    };
                    // filter workitems according to the tab we have to show them in
                    if (
                        (WorkItemCategoriesForRequirementsTab.indexOf(workItemList[i].fields[WorkItemType]) >= 0 ||
                            WorkItemTypeReferenceNamesForRequirementsTab.indexOf(workItemTypeRefMap.get(workItemList[i].fields[WorkItemType]) as string) >= 0) &&
                        action.payload.selectedTabId === Constants.RequirementsTabId
                    ) {
                        linkedRequirements.push(linkedWorkItem);
                    }
                    if (
                        (WorkItemCategoriesForBugsTab.indexOf(workItemList[i].fields[WorkItemType]) >= 0 ||
                            WorkItemTypeReferenceNamesForBugsTab.indexOf(workItemTypeRefMap.get(workItemList[i].fields[WorkItemType]) as string) >= 0) &&
                        action.payload.selectedTabId === Constants.BugsTabId
                    ) {
                        linkedBugs.push(linkedWorkItem);
                    }
                }
            }
        }
    }
    if (action.payload.selectedTabId === Constants.RequirementsTabId) {
        yield effects.put(TestCaseLinkedItemsActions.fetchLinkedRequirementsSucceeded(linkedRequirements));
    }
    if (action.payload.selectedTabId === Constants.BugsTabId) {
        yield effects.put(TestCaseLinkedItemsActions.fetchLinkedBugsSucceeded(linkedBugs));
    }

    const linkedWorkItemsPayload = {
        testCaseId: action.payload.testCaseId,
        linkedRequirements: linkedRequirements,
        selectedTabId: action.payload.selectedTabId,
        linkedBugs: linkedBugs
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.TEST_CASE_LINKED_WORKITEMS, linkedWorkItemsPayload));
}

export function* saveSelectedTab(action: ActionsOfType<TestCaseLinkedItemsActions, TestCaseLinkedItemsActionsTypes.saveSelectedTab>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const settingsService: ISettingsService = (yield effects.call([pageContext, pageContext.getService], "ISettingsService")) as any;
    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;
    let registryEntries;
    registryEntries = {
        LinkedPanelLastSelectedTab: action.payload.selectedTabId
    };
    yield effects.call([settingsService, settingsService.setEntries], registryEntries, SettingsUserScope.Me, "project", tfsPageData.project.id);
}

export function* fetchLinkedTestSuites(
    action: ActionsOfType<TestCaseLinkedItemsActions, TestCaseLinkedItemsActionsTypes.fetchLinkedTestSuites>
): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;

    const testSuites: TestSuite[] = (yield effects.call([dataService, dataService.getSuitesByTestCaseId], action.payload.testCaseId)) as any;
    yield effects.put(TestCaseLinkedItemsActions.fetchLinkedTestSuitesSucceeded(testSuites));
}

export function* getInitialDataForLinkedPanel(
    action: ActionsOfType<TestCaseLinkedItemsActions, TestCaseLinkedItemsActionsTypes.getInitialDataForLinkedPanel>
): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const settingsService: ISettingsService = (yield effects.call([pageContext, pageContext.getService], "ISettingsService")) as any;
    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;
    let selectedTabId: string = Constants.TestSuitesTabId;
    try {
        // fetch the last selected tab from registery entries
        let result: { [key: string]: any } = (yield effects.call(
            [settingsService, settingsService.getEntriesAsync],
            "",
            SettingsUserScope.Me,
            "project",
            tfsPageData.project.id
        )) as any;
        if (result && result[LinkedPanelLastSelectedTab]) {
            selectedTabId = result[LinkedPanelLastSelectedTab];
        }
    } catch (e) {
        console.log(e);
    }

    yield effects.put(TestCaseLinkedItemsActions.changeSelectedTab(selectedTabId));
    if (selectedTabId !== Constants.TestSuitesTabId) {
        yield effects.put(TestCaseLinkedItemsActions.fetchLinkedRequirementsOrBugs(action.payload.testCaseId, selectedTabId));
    } else {
        yield effects.put(TestCaseLinkedItemsActions.fetchLinkedTestSuites(action.payload.testCaseId));
    }

    const telemetryPayload = {
        testCaseId: action.payload.testCaseId,
        planId: action.payload.planId,
        selectedTabId: selectedTabId
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.TEST_CASE_LINKED_ITEMS, telemetryPayload));
}

const _fetchLinkedRequirementsOrBugs = errorHandledSaga(function* (
    action: ActionsOfType<TestCaseLinkedItemsActions, TestCaseLinkedItemsActionsTypes.fetchLinkedRequirementsOrBugs>
): SagaIterator {
    yield effects.call(fetchLinkedRequirementsOrBugs, action);
});

const _saveSelectedTab = errorHandledSaga(function* (
    action: ActionsOfType<TestCaseLinkedItemsActions, TestCaseLinkedItemsActionsTypes.saveSelectedTab>
): SagaIterator {
    yield effects.call(saveSelectedTab, action);
});

const _fetchLinkedTestSuites = errorHandledSaga(function* (
    action: ActionsOfType<TestCaseLinkedItemsActions, TestCaseLinkedItemsActionsTypes.fetchLinkedTestSuites>
): SagaIterator {
    yield effects.call(fetchLinkedTestSuites, action);
});

const _getInitialDataForLinkedPanel = errorHandledSaga(function* (
    action: ActionsOfType<TestCaseLinkedItemsActions, TestCaseLinkedItemsActionsTypes.getInitialDataForLinkedPanel>
): SagaIterator {
    yield effects.call(getInitialDataForLinkedPanel, action);
});